import {ComponentStore} from '@ngrx/component-store';
import {Injectable} from '@angular/core';
import {DashboardWidget, DashboardWidgetConfig, DashboardWidgetConfigData, ReportDashboardState} from './report-dashboard';
import {ReportStore} from './report.store';
import {Actions, ofType} from '@ngrx/effects';
import {Observable, of, Subscription, switchMap, tap, withLatestFrom} from 'rxjs';
import {AdReportParams} from '../../interfaces/ad-reports';
import {ReportStoreState} from './report';
import {filter, first} from 'rxjs/operators';
import {ReportDashboardService} from '../../services/report-dashboard.service';
import * as lodash from 'lodash';
import {ComponentType} from '@angular/cdk/overlay';
import {reportDashboardAddWidget, reportDashboardUpdateWidgets} from './report.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {AdRRequestParams} from '../../classes/ad-r-request-params';
import {dialogOpen} from "../dialog/dialog.actions";
import {AddWidgetDialogComponent} from "../../../reports/dashboard-report/dashboard-report/add-widget-dialog/add-widget-dialog.component";
import {AppState} from "../store";
import {Store} from "@ngrx/store";
import {
  MoveWidgetsDialogComponent
} from "../../../reports/dashboard-report/dashboard-report/move-widgets-dialog/move-widgets-dialog.component";
import {ExitReportGuardService} from "../../services/exit-report-guard.service";

@Injectable()
export class ReportDashboardStore extends ComponentStore<ReportDashboardState> {
  public readonly dashboard$: Observable<Array<DashboardWidget>> = this.select((state: ReportDashboardState): Array<DashboardWidget> => state.dashboard);
  public readonly save$: Observable<Array<DashboardWidget>> = this.select((state: ReportDashboardState): Array<DashboardWidget> => state.save);

  public readonly updateWidgets: (dashboard: Partial<DashboardWidget>[]) => void = this.effect((params$: Observable<DashboardWidget[]>) => params$.pipe(
    withLatestFrom(this.state$),
    tap(([dashboard, state]: [DashboardWidget[], ReportDashboardState]): void => {
      for (let i: number = 0; i < dashboard.length; i++) {
        state.dashboard[i].x = dashboard[i].x;
        state.dashboard[i].y = dashboard[i].y;
        state.dashboard[i].rows = dashboard[i].rows;
        state.dashboard[i].cols = dashboard[i].cols;
      }

      this._exitReportGuardService.canExitBehaviorSubject.next(false);

      this.patchState({
        dashboard: [...state.dashboard],
        save: this._dashboardAdapterForSave(state.dashboard)
      });
    })
  ));

  public readonly update: () => void = this.effect((params$: Observable<void>) => params$.pipe(
    withLatestFrom(this.state$),
    tap(([_, state]: [void, ReportDashboardState]): void => {
      this.patchState({
        save: this._dashboardAdapterForSave(state.dashboard)
      });
    })
  ));

  public readonly addWidget: (params: { widget: ComponentType<any>, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams> }) => void = this.effect((widget$: Observable<{ widget: ComponentType<any>, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams> }>): Observable<[{ widget: ComponentType<any>, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams> }, ReportDashboardState]> => widget$.pipe(
    withLatestFrom(this.state$),
    tap(([params, state]: [{ widget: ComponentType<any>, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams> }, ReportDashboardState]): void => {
    })
  ));

  public readonly updateWidget: (params: { widget: DashboardWidget, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams> }) => void = this.effect((params$: Observable<{ widget: DashboardWidget, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams> }>): Observable<[{ widget: DashboardWidget, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams> }, ReportDashboardState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([params, state]: [{ widget: DashboardWidget, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams> }, ReportDashboardState]): void => {
    })
  ));

  public readonly deleteWidget: (widget: DashboardWidget) => void = this.effect((widget$: Observable<DashboardWidget>) => widget$.pipe(
    withLatestFrom(this.state$),
    tap(([widgetToDelete, state]: [DashboardWidget, ReportDashboardState]): void => {
      const dashboard: Array<DashboardWidget> = state.dashboard.filter((widget: DashboardWidget): boolean => widget !== widgetToDelete);

      this._exitReportGuardService.canExitBehaviorSubject.next(false);

      this.patchState({
        dashboard: dashboard,
        save: this._dashboardAdapterForSave(dashboard)
      });
    })
  ));

  private readonly _init$: Subscription = this.effect((): Observable<Array<DashboardWidget>> => this._reportStore.reportParams$.pipe(
    withLatestFrom(this._reportStore.state$),
    filter(([_, reportStoreState]: [Partial<AdReportParams>, ReportStoreState]): boolean => reportStoreState.type && reportStoreState.submitted && !!reportStoreState.requestParams.dashboard),
    first(),
    switchMap(([_, reportStoreState]: [Partial<AdReportParams>, ReportStoreState]): Observable<Array<DashboardWidget>> => this._service.savedDashboardAdapter(lodash.cloneDeep(reportStoreState.requestParams.dashboard)).pipe(
      tap((dashboard: Array<DashboardWidget>): void => {
        this.patchState({
          dashboard: dashboard,
          save: reportStoreState.requestParams.dashboard
        });
      })
    ))
  ));

  private readonly _save$: Subscription = this.effect(() => this.save$.pipe(
    tap((save: Array<DashboardWidget>): void => {
      this._reportStore.updateRequestParamsSubmitted({
        dashboard: save
      });
    })
  ));

  private readonly _addWidget$: Subscription = this.effect(() => this._actions$.pipe(
    ofType(reportDashboardAddWidget),
    withLatestFrom(this.state$),
    tap(([action, state]: [TypedAction<string> & { widget: ComponentType<any>, config: Partial<DashboardWidgetConfig>, params: Partial<AdReportParams>, dashboard: Partial<DashboardWidget>[] }, ReportDashboardState]): void => {
      const widgetAdded: Partial<DashboardWidget> = action.dashboard.pop();

      for (let i: number = 0; i < action.dashboard.length; i++) {
        state.dashboard[i].x = action.dashboard[i].x;
        state.dashboard[i].y = action.dashboard[i].y;
        state.dashboard[i].rows = action.dashboard[i].rows;
        state.dashboard[i].cols = action.dashboard[i].cols;
      }

      const dashboard: DashboardWidget[] = [
        ...state.dashboard,
        {
          cols: widgetAdded.cols,
          rows: widgetAdded.rows,
          x: widgetAdded.x,
          y: widgetAdded.y,
          config: {
            widget: action.widget,
            ...action.config
          },
          params: action.params
        }
      ];

      this._exitReportGuardService.canExitBehaviorSubject.next(false);

      this.patchState({
        dashboard: dashboard,
        save: this._dashboardAdapterForSave(dashboard)
      });
    })
  ));

  private _updateWidgets$: Subscription = this.effect((): Observable<[TypedAction<string> & { dashboard: DashboardWidget[] }, ReportDashboardState]> => this._actions$.pipe(
    ofType(reportDashboardUpdateWidgets),
    withLatestFrom(this.state$),
    tap(([action, state]: [TypedAction<string> & { dashboard: DashboardWidget[] }, ReportDashboardState]): void => {

      for (let i: number = 0; i < action.dashboard.length; i++) {
        state.dashboard[i].x = action.dashboard[i].x;
        state.dashboard[i].y = action.dashboard[i].y;
        state.dashboard[i].rows = action.dashboard[i].rows;
        state.dashboard[i].cols = action.dashboard[i].cols;
      }

      const dashboard: DashboardWidget[] = [...state.dashboard];

      this._exitReportGuardService.canExitBehaviorSubject.next(false);

      this.patchState({
        dashboard: dashboard,
        save: this._dashboardAdapterForSave(dashboard)
      });
    })
  ));

  public configureAddWidget: () => {} = this.effect((params$: Observable<void>) => params$.pipe(
    withLatestFrom(this.dashboard$),
    tap(([_, dashboard]: [void, DashboardWidget[]]): void => {
      this._store.dispatch(dialogOpen({
        component: AddWidgetDialogComponent,
        config: {
          width: '750px',
          height: 'auto'
        },
        data$: {
          mode: of('add'),
          dashboard: this.dashboard$
        }
      }));
    })
  ));

  public configureMoveWidgets: () => {} = this.effect((params$: Observable<void>) => params$.pipe(
    withLatestFrom(this.dashboard$),
    tap(([_, dashboard]: [void, DashboardWidget[]]): void => {
      this._store.dispatch(dialogOpen({
        component: MoveWidgetsDialogComponent,
        config: {
          width: '750px',
          height: 'auto'
        },
        data: {
          dashboard
        }
      }));
    })
  ));

  constructor(
    private readonly _reportStore: ReportStore,
    private readonly _actions$: Actions,
    private readonly _service: ReportDashboardService,
    private readonly _store: Store<AppState>,
    private readonly _exitReportGuardService: ExitReportGuardService
  ) {
    super({
      dashboard: [],
      save: []
    });
  }

  private _dashboardAdapterForSave(dashboard: Array<DashboardWidget>): Array<DashboardWidget> {
    const save: Array<DashboardWidget> = [];

    for (const widget of dashboard) {
      save.push({
        cols: widget.cols,
        rows: widget.rows,
        x: widget.x,
        y: widget.y,
        config: {
          ...widget.config,
          widget: this._service.getWidgetText(widget.config.widget as ComponentType<any>),
          data: widget.config.data?.map((data: DashboardWidgetConfigData): DashboardWidgetConfigData => {
            const newData: any = { ...data };

            delete newData.params;
            return newData;
          }) || null
        }
      });
    }

    return save;
  }

}
